import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { parseISO, differenceInDays, addDays, startOfDay, format, isValid } from "date-fns";
import { it } from "date-fns/locale";
import { trafficLight, fmtEUR } from "../lib/format";
import { Building2, User, Filter, X, ArrowUpDown, Move } from "lucide-react";
import { toast } from "sonner";

const DAY_WIDTH = 26; // px per day
const ROW_HEIGHT = 44;

const SORT_OPTIONS = [
  { key: "deadline_asc", label: "Scadenza (più vicina)" },
  { key: "deadline_desc", label: "Scadenza (più lontana)" },
  { key: "revenue_desc", label: "Parcella (alta → bassa)" },
  { key: "revenue_asc", label: "Parcella (bassa → alta)" },
  { key: "client_asc", label: "Cliente (A → Z)" },
  { key: "created_desc", label: "Creazione (recenti)" },
  { key: "created_asc", label: "Creazione (vecchie)" },
];

const parseDateSafe = (iso) => { try { const d = parseISO(iso); return isValid(d) ? d : null; } catch { return null; } };

export default function Timeline() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState({});
  const [filterClient, setFilterClient] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem("timeline_sort") || "deadline_asc"; } catch { return "deadline_asc"; }
  });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/clients").then((r) => setClients(Object.fromEntries(r.data.map((c) => [c.id, c]))));
  }, []);

  useEffect(() => { try { localStorage.setItem("timeline_sort", sortKey); } catch {} }, [sortKey]);

  // Milestone drag-to-reschedule (state defined here; effect below after `days` is available)
  const [drag, setDrag] = useState(null); // { projectId, milestoneId, startX, originalIdx, currentIdx }

  const beginDrag = (e, projectId, milestone, mi) => {
    if (milestone.completed) return; // completed milestones locked
    e.preventDefault();
    e.stopPropagation();
    setDrag({
      projectId,
      milestoneId: milestone.id,
      milestoneName: milestone.name,
      startX: e.clientX,
      originalIdx: mi,
      currentIdx: mi,
    });
  };

  const filteredProjects = useMemo(() => {
    let arr = projects.filter((p) => {
      if (filterClient && p.client_id !== filterClient) return false;
      if (filterType && p.type !== filterType) return false;
      if (filterStatus) {
        const tl = trafficLight(p.deadline, p.delivered);
        if (tl.level !== filterStatus) return false;
      }
      return true;
    });
    const HUGE = 8_640_000_000_000_000; // Number.MAX_SAFE-ish for missing dates
    arr = [...arr].sort((a, b) => {
      switch (sortKey) {
        case "deadline_asc": {
          const da = parseDateSafe(a.deadline)?.getTime() ?? HUGE;
          const db = parseDateSafe(b.deadline)?.getTime() ?? HUGE;
          return da - db;
        }
        case "deadline_desc": {
          const da = parseDateSafe(a.deadline)?.getTime() ?? -HUGE;
          const db = parseDateSafe(b.deadline)?.getTime() ?? -HUGE;
          return db - da;
        }
        case "revenue_desc": return (b.parcella_calculated || 0) - (a.parcella_calculated || 0);
        case "revenue_asc": return (a.parcella_calculated || 0) - (b.parcella_calculated || 0);
        case "client_asc": {
          const ca = (clients[a.client_id]?.name || "").toLowerCase();
          const cb = (clients[b.client_id]?.name || "").toLowerCase();
          return ca.localeCompare(cb);
        }
        case "created_desc": {
          const da = parseDateSafe(a.created_at)?.getTime() ?? 0;
          const db = parseDateSafe(b.created_at)?.getTime() ?? 0;
          return db - da;
        }
        case "created_asc": {
          const da = parseDateSafe(a.created_at)?.getTime() ?? HUGE;
          const db = parseDateSafe(b.created_at)?.getTime() ?? HUGE;
          return da - db;
        }
        default: return 0;
      }
    });
    return arr;
  }, [projects, clients, filterClient, filterType, filterStatus, sortKey]);

  const activeFilters = filterClient || filterType || filterStatus;
  const clearFilters = () => { setFilterClient(""); setFilterType(""); setFilterStatus(""); };

  const { start, end, days, rows } = useMemo(() => {
    const all = [];
    filteredProjects.forEach((p) => {
      if (p.deadline) {
        try {
          const d = parseISO(p.deadline);
          if (isValid(d)) all.push(d);
        } catch {}
      }
      (p.milestones || []).forEach((m) => {
        if (m.due_date) {
          try {
            const d = parseISO(m.due_date);
            if (isValid(d)) all.push(d);
          } catch {}
        }
      });
    });
    const now = startOfDay(new Date());
    let s = now, e = addDays(now, 45);
    if (all.length) {
      const minDate = new Date(Math.min(...all.map((d) => d.getTime())));
      const maxDate = new Date(Math.max(...all.map((d) => d.getTime())));
      s = startOfDay(new Date(Math.min(minDate.getTime(), now.getTime() - 7 * 86400000)));
      e = startOfDay(new Date(Math.max(maxDate.getTime() + 7 * 86400000, now.getTime() + 30 * 86400000)));
    }
    const totalDays = Math.max(30, differenceInDays(e, s) + 1);
    const dayList = Array.from({ length: totalDays }, (_, i) => addDays(s, i));
    return { start: s, end: e, days: dayList, rows: filteredProjects };
  }, [filteredProjects]);

  const dayIndex = (iso) => {
    if (!iso) return null;
    try {
      const d = parseISO(iso);
      if (!isValid(d)) return null;
      return differenceInDays(startOfDay(d), start);
    } catch { return null; }
  };
  const todayIdx = differenceInDays(startOfDay(new Date()), start);

  // Drag handler (needs `days` and `start` from the memo above)
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const deltaDays = Math.round((e.clientX - drag.startX) / DAY_WIDTH);
      const newIdx = Math.max(0, Math.min(days.length - 1, drag.originalIdx + deltaDays));
      if (newIdx !== drag.currentIdx) setDrag((d) => d ? { ...d, currentIdx: newIdx } : d);
    };
    const onUp = async () => {
      const cur = drag;
      setDrag(null);
      if (!cur || cur.currentIdx === cur.originalIdx) return;
      const newDate = addDays(start, cur.currentIdx);
      const newIso = newDate.toISOString();
      const proj = projects.find((p) => p.id === cur.projectId);
      const ms = proj?.milestones?.find((m) => m.id === cur.milestoneId);
      if (!ms) return;
      // Optimistic update
      setProjects((prev) => prev.map((p) => p.id === cur.projectId ? {
        ...p,
        milestones: (p.milestones || []).map((m) => m.id === cur.milestoneId ? { ...m, due_date: newIso } : m),
      } : p));
      try {
        await api.put(`/projects/${cur.projectId}/milestones/${cur.milestoneId}`, {
          name: ms.name,
          percentage: ms.percentage,
          due_date: newIso,
          completed: ms.completed,
          completed_at: ms.completed_at,
        });
        toast.success(`Milestone spostata al ${format(newDate, "d MMM yyyy", { locale: it })}`);
      } catch {
        toast.error("Errore salvataggio milestone — ricaricando");
        api.get("/projects").then((r) => setProjects(r.data));
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, days.length, start, projects]);

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up" data-testid="timeline-page">
      <header>
        <div className="mono-label">Scadenzario</div>
        <h1 className="font-display text-4xl font-medium mt-1">Timeline Gantt</h1>
        <p className="text-sm text-neutral-500 mt-2">Panoramica cronologica di ogni gara e milestone. Trascina per scorrere.</p>
      </header>

      {/* Filters */}
      <div className="border border-neutral-200 bg-white p-4 flex items-center gap-3 flex-wrap">
        <div className="mono-label flex items-center gap-1.5"><Filter className="h-3 w-3" /> Filtri</div>
        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} data-testid="tl-filter-client" className="border border-neutral-300 px-2 py-1.5 text-sm bg-white">
          <option value="">Tutti i clienti</option>
          {Object.values(clients).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="tl-filter-type" className="border border-neutral-300 px-2 py-1.5 text-sm bg-white">
          <option value="">Tutti i tipi</option>
          <option value="pa">Pubblica Amm.</option>
          <option value="private">Privato</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="tl-filter-status" className="border border-neutral-300 px-2 py-1.5 text-sm bg-white">
          <option value="">Tutti gli stati</option>
          <option value="red">Rosso</option>
          <option value="orange">Arancione</option>
          <option value="yellow">Giallo</option>
          <option value="green">Consegnata</option>
          <option value="none">Senza scadenza</option>
        </select>
        <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-neutral-200">
          <span className="mono-label flex items-center gap-1.5"><ArrowUpDown className="h-3 w-3" /> Ordina</span>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} data-testid="tl-sort" className="border border-neutral-300 px-2 py-1.5 text-sm bg-white">
            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        {activeFilters && (
          <button onClick={clearFilters} data-testid="tl-filter-clear" className="text-xs text-neutral-600 hover:text-neutral-900 flex items-center gap-1 ml-auto">
            <X className="h-3 w-3" /> Reset ({filteredProjects.length} di {projects.length})
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="py-16 text-center text-sm text-neutral-500 border border-dashed">Nessun progetto ancora.</div>
      ) : (
        <div className="border border-neutral-200 bg-white overflow-hidden">
          <div className="flex">
            {/* Left labels column */}
            <div className="w-80 flex-shrink-0 border-r border-neutral-200">
              <div className="h-12 border-b border-neutral-200 flex items-center px-4 mono-label bg-neutral-50">Gara / Cliente</div>
              {rows.map((p) => {
                const tl = trafficLight(p.deadline, p.delivered);
                const cl = clients[p.client_id];
                return (
                  <Link key={p.id} to={`/projects/${p.id}`} data-testid={`timeline-row-${p.id}`}
                    className="flex items-center gap-2 px-4 border-b border-neutral-100 hover:bg-neutral-50" style={{ height: ROW_HEIGHT }}>
                    <div className={`h-2 w-2 tl-bar-${tl.level} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{p.title}</div>
                      <div className="mono-label truncate flex items-center gap-1">
                        {p.type === "pa" ? <Building2 className="h-2.5 w-2.5" /> : <User className="h-2.5 w-2.5" />}
                        {cl?.name || "—"}
                      </div>
                    </div>
                    <div className="font-mono text-[10px] text-neutral-500">{fmtEUR(p.parcella_calculated)}</div>
                  </Link>
                );
              })}
            </div>

            {/* Right gantt panel */}
            <div className="flex-1 overflow-x-auto">
              <div style={{ width: days.length * DAY_WIDTH, minWidth: "100%" }}>
                {/* Header days */}
                <div className="h-12 border-b border-neutral-200 flex bg-neutral-50 relative">
                  {days.map((d, i) => {
                    const isMonthStart = d.getDate() === 1;
                    const isToday = i === todayIdx;
                    const dKey = d.toISOString().slice(0, 10);
                    return (
                      <div key={dKey} style={{ width: DAY_WIDTH }}
                        className={`flex flex-col items-center justify-center text-[10px] border-r ${isMonthStart ? "border-neutral-300" : "border-neutral-100"} ${isToday ? "bg-orange-50" : ""}`}>
                        <div className="mono-label leading-none">{format(d, "d", { locale: it })}</div>
                        {(isMonthStart || i === 0) && (
                          <div className="font-mono text-[9px] text-neutral-500 uppercase mt-0.5 whitespace-nowrap">{format(d, "MMM yy", { locale: it })}</div>
                        )}
                      </div>
                    );
                  })}
                  {todayIdx >= 0 && todayIdx < days.length && (
                    <div className="absolute top-0 bottom-0 w-px bg-orange-500 z-10" style={{ left: todayIdx * DAY_WIDTH + DAY_WIDTH / 2 }} />
                  )}
                </div>

                {/* Rows */}
                {rows.map((p) => {
                  const tl = trafficLight(p.deadline, p.delivered);
                  const dIdx = dayIndex(p.deadline);
                  return (
                    <div key={p.id} className="relative border-b border-neutral-100 hover:bg-neutral-50" style={{ height: ROW_HEIGHT }}>
                      {/* Weekend/grid subtle background */}
                      {days.map((d, i) => (
                        <div key={d.toISOString().slice(0, 10)} style={{ left: i * DAY_WIDTH, width: DAY_WIDTH }}
                          className={`absolute top-0 bottom-0 border-r ${d.getDate() === 1 ? "border-neutral-200" : "border-neutral-50"} ${[0, 6].includes(d.getDay()) ? "bg-neutral-50/70" : ""}`} />
                      ))}
                      {todayIdx >= 0 && todayIdx < days.length && (
                        <div className="absolute top-0 bottom-0 w-px bg-orange-400 z-10" style={{ left: todayIdx * DAY_WIDTH + DAY_WIDTH / 2 }} />
                      )}
                      {/* Milestones */}
                      {(p.milestones || []).map((m) => {
                        const originalMi = dayIndex(m.due_date);
                        if (originalMi == null || originalMi < 0 || originalMi >= days.length) return null;
                        const isDragging = drag && drag.projectId === p.id && drag.milestoneId === m.id;
                        const mi = isDragging ? drag.currentIdx : originalMi;
                        return (
                          <div
                            key={m.id}
                            data-testid={`ms-${m.id}`}
                            title={m.completed ? `${m.name} · ${m.percentage}% (completata)` : `${m.name} · ${m.percentage}% · trascina per riprogrammare`}
                            onMouseDown={(e) => beginDrag(e, p.id, m, originalMi)}
                            className={`absolute top-1/2 -translate-y-1/2 h-3 w-3 rotate-45 border select-none ${m.completed ? "brand-bg brand-border cursor-not-allowed" : "bg-white border-neutral-500 cursor-grab hover:scale-125 transition-transform"} ${isDragging ? "!cursor-grabbing scale-125 ring-2 ring-orange-400" : ""}`}
                            style={{ left: mi * DAY_WIDTH + DAY_WIDTH / 2 - 6, zIndex: isDragging ? 20 : 5 }}
                          />
                        );
                      })}
                      {/* Drag preview label */}
                      {drag && drag.projectId === p.id && (
                        <div
                          className="absolute -top-1 bg-orange-500 text-white font-mono text-[9px] px-1.5 py-0.5 rounded-sm pointer-events-none z-30 whitespace-nowrap"
                          style={{ left: drag.currentIdx * DAY_WIDTH + DAY_WIDTH / 2 + 4 }}
                        >
                          {format(addDays(start, drag.currentIdx), "d MMM yyyy", { locale: it })}
                        </div>
                      )}
                      {/* Main deadline bar */}
                      {dIdx != null && dIdx >= 0 && dIdx < days.length && (
                        <div className={`absolute top-1/2 -translate-y-1/2 h-6 px-2 flex items-center border font-mono text-[10px] font-semibold ${tl.className}`}
                          style={{
                            left: Math.max(0, dIdx - 3) * DAY_WIDTH,
                            width: Math.min(6, dIdx + 1) * DAY_WIDTH,
                          }}>
                          <span className="truncate">{tl.level === "green" ? "CONS." : format(parseISO(p.deadline), "dd MMM", { locale: it })}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-neutral-600 flex-wrap">
        <div className="flex items-center gap-2"><div className="h-3 w-3 rotate-45 brand-bg" /> Milestone completata</div>
        <div className="flex items-center gap-2"><div className="h-3 w-3 rotate-45 border border-neutral-500 bg-white" /> Milestone da fare</div>
        <div className="flex items-center gap-2"><Move className="h-3 w-3" /> Trascina le milestone per riprogrammarle</div>
        <div className="flex items-center gap-2"><div className="h-px w-6 bg-orange-500" /> Oggi</div>
      </div>
    </div>
  );
}
